// useAiMemory — read/write the signed-in user's "Custom AI Instructions"
// stored in `user_ai_memory`. Used on /settings and (potentially) anywhere
// else we want to surface or quick-edit the user's prompt context.
//
// We hard-cap the input at 4000 chars to match the backend trim and avoid
// blowing out the model context.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";

export const AI_MEMORY_MAX_CHARS = 4000;

export function useAiMemory() {
  const { user } = useAuth();
  const [instructions, setInstructions] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!user) {
      setInstructions("");
      setLoading(false);
      return;
    }
    setLoading(true);
    (async () => {
      const { data, error } = await supabase
        .from("user_ai_memory")
        .select("custom_instructions")
        .eq("user_id", user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.warn("useAiMemory load failed:", error.message);
        setInstructions("");
      } else {
        setInstructions((data?.custom_instructions ?? "").toString());
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const save = useCallback(
    async (next: string) => {
      if (!user) {
        toast.error("Sign in to save your AI memory");
        return false;
      }
      const trimmed = next.slice(0, AI_MEMORY_MAX_CHARS);
      setSaving(true);
      // upsert by primary key (user_id) — works for both first save and edits.
      const { error } = await supabase.from("user_ai_memory").upsert(
        { user_id: user.id, custom_instructions: trimmed },
        { onConflict: "user_id" },
      );
      setSaving(false);
      if (error) {
        toast.error("Couldn't save", { description: error.message });
        return false;
      }
      setInstructions(trimmed);
      toast.success("AI memory saved");
      return true;
    },
    [user],
  );

  const clear = useCallback(async () => {
    return save("");
  }, [save]);

  return { instructions, setInstructions, save, clear, loading, saving };
}
